import { gsap } from "gsap";
import { Container, IDestroyOptions, Point, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { GameFont } from "../../../../common/GameFont";
import { BasePageView } from "../../../../common/components/BasePageView";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { SoundsResourcePackageWrapper } from "../../../../common/resources/SoundsResourcePackageWrapper";
import { FadeScreen } from "../../../../game/components/FadeScreen";
import { StealCard } from "../../../../game/flow/impl/diceActions/stealAction/StealCard";
import { Card } from "../../components/Card";
import { Girl } from "../../components/Girl";
import { LightComponent } from "../../components/LightComponent";
import { StealResourcePackage } from "../../resources/StealResourcePackage";
import { UserProfileResourcePackage } from "../../../userprofile/resources/UserProfileResourcePackage";
import { IStealController } from "../../controllers/IStealController";
import { TickerDelay } from "../../../../../../tools/TickerDelay";
import { CoinsCollectAnimationView } from "../../../../common/components/animations/CoinsCollectAnimationView";
import { TopPanel } from "../../../../game/components/toppanel/TopPanel";

export class MainStealPage extends BasePageView {
    private static readonly SELECT_CARD_TEXT = "SELECT A CARD";

    private readonly stealResources: StealResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly stealController: IStealController;
    private readonly viewCompletionSource: PromiseCompletionSource<StealCard>;
    private readonly topPanel: TopPanel;
    private avatarTextures: Array<Texture>;
    private fadeScreen: FadeScreen;
    private cardsContainer: Container;
    private cards: Array<Card>;
    private girl: Girl;
    private cardsMoveStartPosition: Point;
    private tooltipText: Text;
    private light: LightComponent;
    private backgroundLight: Sprite;
    private stelaCardsData: Array<StealCard>;
    private coinsCollectPoint: Point;
    private coinsCollectAnimation: CoinsCollectAnimationView;
    private currentAnimation: gsap.core.Timeline;

    public constructor(
        canvasScaler: ICanvasScaler,
        stealResources: StealResourcePackage,
        commonResources: CommonResourcesPackage,
        soundResources: SoundsResourcePackageWrapper,
        userProfileResources: UserProfileResourcePackage,
        stealController: IStealController,
        topPanel: TopPanel,
        viewCompletionSource: PromiseCompletionSource<StealCard>
    ) {
        super(canvasScaler);
        this.stealResources = stealResources;
        this.commonResources = commonResources;
        this.soundResources = soundResources;
        this.userProfileResources = userProfileResources;
        this.stealController = stealController;
        this.topPanel = topPanel;
        this.viewCompletionSource = viewCompletionSource;
        this.cards = [];
        this.stelaCardsData = [];
        this.initAvatarTextures();
        this.coinsCollectPoint = new Point();

        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.once("added", this.showFirstComponents.bind(this));
        this.eventMode = "static";
        this.light.eventMode = "none";
        this.on("pointerup", this.skipAnimations, this);
    }

    private initAvatarTextures(): void {
        this.avatarTextures = [
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0065_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0066_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0067_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0068_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0069_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0070_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0071_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0072_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0073_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0074_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0242_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0243_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0244_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0245_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0246_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0247_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0248_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0249_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0250_png,
            this.stealResources.assets_views_steal_popup_avatars_profile_icons_0251_png
        ];
    }

    protected override build(): void {
        this.fadeScreen = new FadeScreen(0.3, 0, 0.7, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.fadeScreen);

        this.backgroundLight = new Sprite(this.stealResources.assets_views_steal_popup_light_bg_png);
        this.backgroundLight.anchor.set(0.5);
        this.addChild(this.backgroundLight);

        this.girl = new Girl(this.stealResources);
        this.addChild(this.girl);

        this.tooltipText = new Text(MainStealPage.SELECT_CARD_TEXT, new TooltipTextStyle());
        this.tooltipText.anchor.set(0.5, 0.5);
        this.addChild(this.tooltipText);

        this.cardsContainer = new Container();
        this.addChild(this.cardsContainer);

        this.light = new LightComponent(this.stealResources);
        this.light.angle -= 21;
        this.addChild(this.light);

        this.cardsContainer.addChild(this.cardContainerFactory());
        this.cardsContainer.addChild(this.cardContainerFactory());
        this.cardsContainer.addChild(this.cardContainerFactory());
        this.cardsContainer.addChild(this.cardContainerFactory());
        this.cardsContainer.addChild(this.cardContainerFactory());
        this.cardsContainer.addChild(this.cardContainerFactory());

        this.coinsCollectAnimation = new CoinsCollectAnimationView(this.commonResources, 15, this.coinsCollectPoint);
        this.addChild(this.coinsCollectAnimation);

        this.topPanel.bringToFront();
        this.topPanel.visible = false;

        super.build();
    }

    private async showFirstComponents(): Promise<void> {
        this.interactiveCards(false);
        this.girl.visible = false;
        this.tooltipText.visible = false;
        this.light.alpha = 0;
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        this.fadeScreen.fadeIn();
        animationTimeLine.fromTo(this.backgroundLight, { alpha: 0 }, { alpha: 1, duration: 0.1 });
        this.cards.forEach(card => {
            animationTimeLine.fromTo(card, { x: this.cardsMoveStartPosition.x, y: this.cardsMoveStartPosition.y, angle: -120 },
                {
                    x: card.position.x,
                    y: card.position.y,
                    angle: card.angle,
                    duration: 0.4,
                    onStart: () => this.soundResources.assets_sounds_Popups_Entering_mp3.play(),
                }, "<25%");
            animationTimeLine.fromTo(card.scale, { x: 0, y: 0 }, { x: card.scale.x, y: card.scale.y, duration: 0.3 }, "<");
        });
        animationTimeLine.set(this.girl, { visible: true });
        animationTimeLine.fromTo(this.girl, { y: this.girl.y + this.girl.height * 0.47 }, { y: this.girl.y, duration: 0.25 });
        animationTimeLine.set(this.tooltipText, { visible: true });
        animationTimeLine.fromTo(this.tooltipText, { y: this.tooltipText.y + this.tooltipText.height * 2, angle: -90 },
            {
                y: this.tooltipText.y,
                angle: this.tooltipText.angle,
                duration: 0.17
            });
        animationTimeLine.add(this.girl.getStartAnimation().play(), "<");
        await animationTimeLine.play();
        animationTimeLine.kill();
        this.interactiveCards(true);
    }

    private async hideComponents(): Promise<void> {
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        this.cards.forEach(card => {
            animationTimeLine.to(card,
                {
                    x: this.cardsMoveStartPosition.x,
                    y: this.cardsMoveStartPosition.y,
                    width: 0,
                    height: 0,
                    angle: -120,
                    duration: 0.3,
                    ease: "power1.in"
                }, "<25%");
        });
        await animationTimeLine;
        animationTimeLine.kill();
    }

    private async startSwitchAndUpdateCards(chosenCard: Card): Promise<void> {
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        animationTimeLine.delay(0.2);
        let cardsData: Array<StealCard> = [];
        cardsData.push(...this.stelaCardsData);
        const willBeChosenIndex = cardsData.findIndex((item) => item.willBeChosen === true);
        if (willBeChosenIndex != -1) {
            cardsData.splice(willBeChosenIndex, 1);
        }
        let index = 0;
        this.cards.forEach(card => {
            if (card != chosenCard) {
                animationTimeLine.to(card.scale, { x: 0, duration: 0.2, yoyo: true, repeat: 1 }, "switchCard");
                animationTimeLine.addLabel("switchCard", "<10%");
                animationTimeLine.add(() => {
                    card.updateAndShowCardContent(cardsData[index], this.getRandomAvatarTexture());
                    index++;
                }, "<50%");
            }
        });
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    private async startCollecting(): Promise<void> {
        this.topPanel.visible = true;
        const timeline = this.currentAnimation = new gsap.core.Timeline();
        timeline.add(() => this.topPanel.show());
        timeline.then(() => timeline?.kill());
        timeline.play();
        this.soundResources.assets_sounds_Coins_Top_Bar_mp3.play();
        this.stealController.collectCoins(this.stelaCardsData.find((item) => item.willBeChosen === true).stealAmountInCoins);
        await this.coinsCollectAnimation.startAnimation();
    }

    protected override layout(width: number, height: number): void {
        this.backgroundLight.position.set(width * 0.5, height * 0.5);
        this.girl.position.set(width * 0.5, height * 0.5);
        this.light.position.set(width * 0.5 + 86, height * 0.5 + 46.5);
        this.cardsContainer.position.set(width * 0.5, height * 0.5 + 591.5);
        this.cardsMoveStartPosition = new Point(width, height * 0.5);
        this.tooltipText.position.set(width * 0.5, height * 0.5 - 114);
        this.coinsCollectAnimation.position.set(width * 0.45, height * 0.5);
        this.coinsCollectPoint.set(0, TopPanel.DEFAULT_POSITION_Y - this.coinsCollectAnimation.position.y);
        this.layoutCards();
        super.layout(width, height);
    }

    private layoutLight(angle: number): gsap.core.Timeline {
        const animationTimeLine = new gsap.core.Timeline();
        if (angle > -50 && angle <= -20) {
            this.light.position.set(421.5, 967);
            this.light.angle = 0;
        }
        if (angle < -50) {
            this.light.position.set(281.5, 932.5);
            this.light.angle = 31.5;
        }
        animationTimeLine.fromTo(this.light, { alpha: this.light.alpha }, { alpha: 1, duration: 0.5 });
        animationTimeLine.then(() => animationTimeLine?.kill());
        return animationTimeLine;
    }

    private layoutCards(): void {
        const cardsPerRow = 3;
        let cardIndex = 0;
        for (let row = 0; row < 2; row++) {
            for (let col = 0; col < cardsPerRow; col++) {
                const card = this.cards[cardIndex];
                card.position.x = card.width * 0.9 * col - card.width * 0.9;
                card.position.y = card.height * 0.95 * row - card.height * 0.95;
                cardIndex++;
            }
        }
    }

    private async cardClickHandle(card: Card): Promise<void> {
        this.interactiveCards(false);

        const claimAnimationTimeLine = new gsap.core.Timeline({ repeat: -1 });
        claimAnimationTimeLine.to(card, { x: "+=15", yoyo: true, repeat: 1, duration: 0.15 });
        claimAnimationTimeLine.to(card, { y: "-=10", yoyo: true, repeat: 1, duration: 0.15 }, "<");
        claimAnimationTimeLine.to(card, { x: "-=15", yoyo: true, repeat: 1, duration: 0.15 });
        claimAnimationTimeLine.to(card, { y: "-=10", yoyo: true, repeat: 1, duration: 0.15 }, "<");
        claimAnimationTimeLine.play();

        this.stelaCardsData = await this.stealController.getStealCardData();
        claimAnimationTimeLine.progress(1);
        claimAnimationTimeLine.kill();

        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        animationTimeLine.fromTo(this.tooltipText, { y: this.tooltipText.y, angle: this.tooltipText.angle },
            {
                y: this.tooltipText.y + this.tooltipText.height * 4,
                angle: -90,
                duration: 0.2,
                ease: "power2.in"
            });
        animationTimeLine.set(this.tooltipText, { visible: false });

        await this.playSelectCard(card);
        await this.startSwitchAndUpdateCards(card);
        await animationTimeLine.play();
        animationTimeLine.kill();
        await TickerDelay.delay(100);
        await Promise.all([this.startCollecting(), this.hideComponents()]);
        this.viewCompletionSource.complete(this.stelaCardsData.find((item) => item.willBeChosen === true));
    }

    private async playSelectCard(card: Card): Promise<void> {
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        animationTimeLine.to(card, { y: card.y - 100, x: card.x + 100 * card.rotation, duration: 0.2, yoyo: true, repeat: 1 });
        const angle = this.getAngleByCard(card);
        animationTimeLine.add(this.girl.playHandAnimation(angle).play(), "<");
        animationTimeLine.add(this.layoutLight(angle).play());
        this.soundResources.assets_sounds_Card_Flip_mp3.play();
        animationTimeLine.to(card.scale, { x: 0, duration: 0.25 });
        animationTimeLine.add(() => {
            const willBeChosenCard = this.stelaCardsData.find((item) => item.willBeChosen === true);
            card.updateAndShowCardContent(willBeChosenCard, this.getRandomAvatarTexture());
        });
        animationTimeLine.to(card.scale, { x: 1, duration: 0.25 });
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    private interactiveCards(isLock: boolean): void {
        this.cards.forEach(card => card.eventMode = isLock ? "dynamic" : "none");
    }

    private getAngleByCard(card: Card): number {
        const col = this.cards.indexOf(card) % 3;
        return col === 0 ? -60 : col === 1 ? -20 : 0;
    }

    private cardContainerFactory(): Card {
        const card = new Card(this.stealResources, this.commonResources, this.userProfileResources, this.canvasScaler);
        card.on("pointerup", this.cardClickHandle.bind(this, card));
        this.cards.push(card);
        return card;
    }

    private getRandomAvatarTexture(): Texture {
        const randomAvatarIndex = Math.round(Math.random() * (this.avatarTextures.length - 1));
        const avatar = this.avatarTextures[randomAvatarIndex];
        this.avatarTextures.splice(randomAvatarIndex, 1);
        return avatar;
    }

    private skipAnimations(): void {
        if (this.currentAnimation != null && this.currentAnimation.isActive()) {
            this.currentAnimation.progress(1);
        }
        if (this.coinsCollectAnimation != null && this.coinsCollectAnimation.isActive) {
            this.coinsCollectAnimation.setAnimationProgress(1);
        }
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.off("pointerup", this.skipAnimations, this);
        this.cards.forEach(card => card.off("pointerup"));
        this.topPanel.bringToOriginParent();
        super.destroy(options);
    }
}

class TooltipTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAngle: 0.5,
            dropShadowBlur: 3,
            dropShadowDistance: 2,
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 58
        }));
    }
}
